import React, { useState, useEffect } from 'react';
import { View, Flex, Button, Text, Link, useTheme, Alert } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify';
import VerificationInput from 'react-verification-input';
import { useToast } from 'contexts/toastContext';
import FormHeader from './FormHeader';
import FormFooter from './FormFooter';

const PhoneVerification = ({
  onVerify,
  onSkip,
  onResendCode,
  onBackToSignIn,
  onBackToRegistration,
  phoneNumber,
  validationError,
  dismissValidationError,
}) => {
  const { tokens } = useTheme();
  const { setToastData } = useToast();
  const [verificationCode, setVerificationCode] = useState('');
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const verifyButtonRef = React.useRef(null);
  const alertStyle = React.useMemo(
    () => ({
      marginTop: tokens.space.medium,
    }),
    [tokens.space.medium],
  );

  useEffect(
    () => () => {
      setToastData(null);
    },
    [setToastData],
  );

  useEffect(() => {
    if (shouldAutoSubmit && isValidCode(verificationCode)) {
      verifyButtonRef.current?.click();
      setShouldAutoSubmit(false);
    }
  }, [verificationCode, shouldAutoSubmit]);

  const isValidCode = (code) => code && code.length === 6;

  const handleCodeComplete = (code) => {
    setVerificationCode(code);
    setShouldAutoSubmit(true);
  };

  const handleVerify = async () => {
    if (isValidCode(verificationCode)) {
      setIsLoading(true);
      try {
        await onVerify(verificationCode);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const buttons = [
    {
      id: 'verify-button',
      onClick: handleVerify,
      variation: 'primary',
      size: 'default',
      isFullWidth: true,
      disabled: !verificationCode || verificationCode.length < 6 || isLoading,
      isLoading: isLoading,
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.step1.verifyButton'),
    },
    {
      onClick: onSkip,
      variation: 'default',
      size: 'default',
      isFullWidth: true,
      backgroundColor: tokens.colors.background.primary,
      color: tokens.colors.brand.primary[80],
      borderColor: tokens.colors.brand.primary[80],
      borderWidth: '1px',
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.skipButton'),
    },
    {
      onClick: onBackToSignIn,
      variation: 'link',
      size: 'default',
      isFullWidth: true,
      color: tokens.colors.brand.primary[80],
      style: { textTransform: 'uppercase' },
      text: I18n.get('phoneVerification.step1.backButton'),
    },
  ];

  return (
    <>
      <View padding={tokens.space.large}>
        {/* Logo and Header */}
        <FormHeader titleTranslationKey="phoneVerification.step2.title" />

        {/* Phone Number Display */}
        <View marginTop={tokens.space.large}>
          <Text fontSize={tokens.fontSizes.medium} color={tokens.colors.font.secondary} textAlign="center">
            {I18n.get('phoneVerification.step2.codeSentTo')} {'\u2066'}
            {phoneNumber}
            {'\u2069'}
          </Text>
        </View>
        {/* Back to previous screen button*/}
        <View marginTop={tokens.space.large}>
          <Link
            color={tokens.colors.brand.primary[80]}
            onClick={onBackToRegistration}
            fontSize={tokens.fontSizes.small}
            style={{ textDecoration: 'underline' }}
          >
            {I18n.get('phoneVerification.step2.changePhone')}
          </Link>
        </View>

        {/* Verification Input */}
        <View marginTop={tokens.space.large}>
          <VerificationInput
            length={6}
            validChars="0-9"
            placeholder=""
            autoFocus
            inputProps={{
              inputMode: 'numeric',
              name: 'confirmation_code',
              required: true,
              autoComplete: 'one-time-code',
            }}
            onComplete={handleCodeComplete}
          />
          {validationError && (
            <Alert
              variation="error"
              isDismissible={true}
              hasIcon={true}
              style={alertStyle}
              onDismiss={dismissValidationError}
            >
              {validationError}
            </Alert>
          )}
        </View>

        {/* Action Buttons */}
        <Flex direction="column" gap={tokens.space.medium} marginTop={tokens.space.large}>
          {buttons.map((button, index) => {
            const { text, id, ...buttonProps } = button;
            return (
              <Button key={index} {...buttonProps} ref={id === 'verify-button' ? verifyButtonRef : null}>
                {text}
              </Button>
            );
          })}
        </Flex>
      </View>

      {/* Bottom text section */}
      <FormFooter>
        <Flex direction="row" alignItems="center" gap={tokens.space.xs}>
          <Text fontSize={tokens.fontSizes.small} color={tokens.colors.font.tertiary}>
            {I18n.get('footer.step2.noCodeReceived')}{' '}
          </Text>
          <Link
            color={tokens.colors.brand.primary[80]}
            onClick={onResendCode}
            textDecoration="underline"
            fontSize={tokens.fontSizes.small}
          >
            {I18n.get('footer.step2.resendCode')}
          </Link>
        </Flex>
        <Text fontSize={tokens.fontSizes.small} color={tokens.colors.font.tertiary}>
          {I18n.get('footer.step2.changeNumber')}
        </Text>
      </FormFooter>
    </>
  );
};

export default PhoneVerification;
